import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Space } from '@client/components/_ui/layout/Space';
import { AttestationBulkActions } from '@client/components/attestations/reporting/AttestationBulkActions';
import { ATTESTATION_REPORTING_VIEWS } from '@client/components/attestations/reporting/attestation-built-in-views';
import { exportAttestationsCsv } from '@client/components/attestations/reporting/attestation-export';
import { toActionTarget } from '@client/components/attestations/reporting/reporting-format';
import { buildInitialWhereFromFilters, FilterBar } from '@client/components/filters';
import { TableViewsBar, useEntityBuiltInViews, useTableViews } from '@client/components/table-views';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { usePermissions } from '@client/hooks/auth/usePermissions';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useOrgReportTimezone } from '@client/hooks/business/org/useOrgReportTimezone';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useAreFeatureFlagsSettled, useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { FilterOperatorSchema } from '@shared/schemas/filter';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Download, Send, UserCheck } from 'lucide-react';
import { type Key, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { userAttestationColumns } from './columns';
import { useUserAttestationFilterConfig } from './useUserAttestationFilterConfig';

const UserAttestationSearchSchema = z.object({
    'user-attestation-filters': z
        .array(
            z.object({
                id: z.string(),
                filterId: z.string(),
                operator: FilterOperatorSchema,
                value: z.any(),
            }),
        )
        .optional()
        .catch(undefined),
    view: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/console/$orgId/attestations/user-attestations')({
    staticData: {
        metadata: {
            title: 'User attestations',
        },
        modules: ['attestations'],
        roles: ['org_admin', 'attestations_user'],
        ui: {
            title: 'User attestations',
            subtitle: 'Manage and track user attestations',
            icon: UserCheck,
        },
    },
    validateSearch: zodValidator(UserAttestationSearchSchema),
    component: UserAttestations,
});

type QueryParams = RouterInputs['userAttestation']['listUserAttestations'];

const COLUMN_PERSIST_KEY = 'user-attestations-table-columns';

function UserAttestations() {
    const orgId = useOrgId();
    const navigate = useNavigate();
    const { can } = usePermissions();
    const { trpcUtils } = useTrpc();
    const timezone = useOrgReportTimezone();
    const user = useUser();
    const { formatDate, formatISODate } = useFormatter();
    const searchParams = Route.useSearch();
    // Linked filters (the Overview's scoped drill-downs) are read once: the filter bar takes the
    // parameter out of the URL on mount, before the saved views resolve and apply them.
    const [linkedFilters] = useState(() => searchParams['user-attestation-filters']);
    const canSendAttestations = can('user_attestation:create');
    const canUpdateAttestations = can('user_attestation:update');

    // Attestation reporting: the Outstanding / Overdue / Awaiting review views,
    // the due countdown and warning flags, bulk actions and export. Only a link
    // to one of those views waits for flags to settle, since the views exist only
    // with the flag on; every filter a link or saved view can carry is registered
    // either way, so nothing else waits on PostHog.
    const reportingEnabled = useFeatureFlag('enable-attestation-reporting', false);
    const flagsSettled = useAreFeatureFlagsSettled({ userId: user?.id.toString(), orgId: orgId.toString() });
    const [linkNeedsFlags] = useState(() => ATTESTATION_REPORTING_VIEWS.some((view) => view.id === searchParams.view));
    const [selectedKeys, setSelectedKeys] = useState<Key[]>([]);
    const [exporting, setExporting] = useState(false);

    const { config: filterConfig, ready: filterConfigReady } = useUserAttestationFilterConfig({
        reporting: reportingEnabled,
    });

    const [input, setInput] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: { sentAt: 'desc' },
        where: buildInitialWhereFromFilters(filterConfig, linkedFilters),
    });

    // Postpone the list query until saved views resolve which view loads first, so we fetch once
    // with the resolved where/sort/pageSize instead of unfiltered-then-refiltered (set by onResolved).
    const [viewsReady, setViewsReady] = useState(false);

    const { data, isLoading, refetch } = trpc.userAttestation.listUserAttestations.useQuery(input, {
        enabled: filterConfigReady && viewsReady,
    });

    const columns = useMemo(
        () => userAttestationColumns(formatDate, formatISODate, { reporting: reportingEnabled }),
        [formatDate, formatISODate, reportingEnabled],
    );

    // Bulk actions apply to the rows on screen: any change of filter, sort or page clears the selection.
    useEffect(() => setSelectedKeys([]), [input]);
    const selectedTargets = (data?.rows ?? [])
        .filter((row) => selectedKeys.includes(String(row.id)))
        .map(toActionTarget);

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: !viewsReady || isLoading,
        columns,
        rowKey: (row) => String(row.id),
        queryParams: input,
        onParamsChange: setInput,
        emptyState: { noRecordsTitle: 'No user attestations yet' },
        pagination: {
            position: 'both',
        },
        columnVisibility: {
            enabled: true,
            persistKey: COLUMN_PERSIST_KEY,
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
        showSorterTooltip: false,
        rowSelection:
            reportingEnabled && canUpdateAttestations
                ? {
                      enabled: true,
                      type: 'checkbox',
                      preserveSelectedRowKeys: false,
                      selectedRowKeys: selectedKeys,
                      onChange: (keys) => setSelectedKeys(keys),
                  }
                : undefined,
    });

    const entityBuiltInViews = useEntityBuiltInViews('recipient');
    const builtInViews = useMemo(
        () => (reportingEnabled ? [...entityBuiltInViews, ...ATTESTATION_REPORTING_VIEWS] : entityBuiltInViews),
        [entityBuiltInViews, reportingEnabled],
    );
    const views = useTableViews({
        entityType: 'USER_ATTESTATION',
        config: filterConfig,
        configReady: filterConfigReady && (flagsSettled || !linkNeedsFlags),
        urlFilterKey: 'user-attestation-filters',
        builtInViews,
        initialFilters: linkedFilters,
        initialActiveViewId: searchParams.view,
        queryParams: input,
        setQueryParams: setInput,
        table: tableProps,
        columnPersistKey: COLUMN_PERSIST_KEY,
        defaultOrderBy: { sentAt: 'desc' },
        defaultPageSize: DEFAULT_PAGE_SIZE,
        onResolved: () => setViewsReady(true),
    });

    return (
        <PageController route={Route}>
            <TableViewsBar controller={views.controller} />

            <TableToolbar
                variant="enhanced"
                filters={
                    <FilterBar
                        config={filterConfig}
                        filters={views.filterState.filters}
                        whereClause={views.filterState.whereClause}
                        onAddFilter={views.filterState.addFilter}
                        onUpdateFilter={views.filterState.updateFilter}
                        onRemoveFilter={views.filterState.removeFilter}
                        onClearFilters={views.filterState.clearFilters}
                    />
                }
                actions={
                    <Space size="small">
                        {reportingEnabled && canUpdateAttestations && (
                            <AttestationBulkActions
                                targets={selectedTargets}
                                actions={['remind', 'extend', 'pass', 'cancel']}
                                onDone={() => setSelectedKeys([])}
                            />
                        )}
                        {reportingEnabled && (
                            <Button
                                type="secondary"
                                icon={<Download />}
                                loading={exporting}
                                onClick={async () => {
                                    setExporting(true);
                                    await exportAttestationsCsv(
                                        trpcUtils.client,
                                        { where: input.where, orderBy: input.orderBy },
                                        'user-attestations',
                                        timezone,
                                    );
                                    setExporting(false);
                                }}
                            >
                                Export
                            </Button>
                        )}
                        {canSendAttestations ? (
                            <Button.Link
                                to="/console/$orgId/attestations/user-attestations/send"
                                params={{ orgId }}
                                variant="primary"
                                icon={<Send />}
                            >
                                Send user attestation
                            </Button.Link>
                        ) : null}
                    </Space>
                }
            />

            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/attestations/user-attestations/$attestationId',
                                params: { orgId, attestationId: id },
                            },
                            e,
                        );
                    },
                })}
            />

            <Outlet />
        </PageController>
    );
}
